import { ElementType } from "react";

import { useUpdateCosignPropertyIsActive } from "manager/hooks/api/cosignQueries";
import { ICosignMappedProperty } from "manager/hooks/api/integrationQueries";
import ConfirmationModal from "shared/components/Modals/ConfirmationModal";
import useModal from "shared/hooks/useModal";

interface UseCosignToggleActionOptions {
  onToggleSuccess?: () => void;
}

interface OpenCosignDisableConfirmationParams {
  property: ICosignMappedProperty;
  openModalDialog: (
    modal: ElementType,
    context: Record<string, unknown>
  ) => void;
  updateCosignPropertyIsActive: (params: {
    cosignPropertyId: number | string;
    isActive: boolean;
  }) => void;
}

export const getCosignDisableConfirmationCopy = (propertyName: string) =>
  `Disabling Cosign for ${propertyName} will prevent new invites from being sent. Existing applications will remain visible.`;

export const openCosignDisableConfirmation = ({
  property,
  openModalDialog,
  updateCosignPropertyIsActive,
}: OpenCosignDisableConfirmationParams) => {
  openModalDialog(ConfirmationModal, {
    title: "Disable Cosign",
    submitButtonLabel: "Disable",
    subtitle: getCosignDisableConfirmationCopy(property.name),
    onConfirm: () =>
      updateCosignPropertyIsActive({
        cosignPropertyId: property.mappingId,
        isActive: false,
      }),
  });
};

export const useCosignToggleAction = ({
  onToggleSuccess,
}: UseCosignToggleActionOptions = {}) => {
  const { openModalDialog } = useModal();
  const { updateCosignPropertyIsActive, isUpdatingCosignPropertyIsActive } =
    useUpdateCosignPropertyIsActive({
      onSuccess: onToggleSuccess,
    });

  const toggleProperty = (
    property: ICosignMappedProperty,
    isActive: boolean
  ) => {
    if (!isActive) {
      openCosignDisableConfirmation({
        property,
        openModalDialog,
        updateCosignPropertyIsActive,
      });
      return;
    }

    updateCosignPropertyIsActive({
      cosignPropertyId: property.mappingId,
      isActive: true,
    });
  };

  return {
    toggleProperty,
    isUpdatingCosignPropertyIsActive,
  };
};
